import { AuthBrowser, AUTH_GOOGLE_OAUTH_QUERY_PARAM, AbService } from "@anydo/auth";
import { AUTH_TOKEN_QUERY_PARAM, omitBrowserLocationParams } from "@anydo/core";
import { I18nLocaleLoader } from "@anydo/i18n-locale-messages";
import { IAnydoAuth } from "@anydo/interfaces";
import { AnydoSentry } from "@anydo/vendor/sentry";
import { GOOGLE_CLIENT_ID } from "./config";

export const authPromise = (async () => {
  if (typeof window === "undefined") return;

  const storage = localStorage;
  const urlSearchParams = new URLSearchParams(window.location.search);

  const abService = new AbService(storage);

  const i18nLocaleLoader = await I18nLocaleLoader.create({
    detectedLocale: localStorage.getItem("locale") || "en",
    storage: localStorage,
  });

  const auth = new AuthBrowser({
    abService,
    storage: sessionStorage,
    i18nLocaleLoader,
    google: {
      clientId: GOOGLE_CLIENT_ID,
    },
  });

  await prepareAuth(urlSearchParams, auth);

  return auth;
})();

function addSearchFromOAuthRedirectState() {
  if (!window.location.hash) return;

  const search = new URLSearchParams(window.location.hash.slice(1));
  const stateParams = search.get("state");

  if (stateParams) {
    const params = new URLSearchParams(stateParams);
    const url = new URL(window.location.href);
    let wasChanged = false;

    for (const [k, v] of params) {
      wasChanged = true;
      url.searchParams.append(k, v);
    }

    if (wasChanged) {
      window.history.replaceState(window.history.state, document.title, url.toString());
    }
  }
}

async function prepareAuth(urlSearchParams: URLSearchParams, auth: AuthBrowser): Promise<void> {
  const token: string | null = urlSearchParams.get(AUTH_TOKEN_QUERY_PARAM);

  if (token != null) {
    omitBrowserLocationParams(AUTH_TOKEN_QUERY_PARAM);
    return auth.loginWithToken(token).then(
      () => {},
      () => auth.logout(),
    );
  }

  if (urlSearchParams.get(AUTH_GOOGLE_OAUTH_QUERY_PARAM)) {
    addSearchFromOAuthRedirectState();
    try {
      await auth.finishGoogleRedirectLogin(window.location.hash);
      omitBrowserLocationParams(AUTH_GOOGLE_OAUTH_QUERY_PARAM);
      return;
    } catch (error) {
      omitBrowserLocationParams(AUTH_GOOGLE_OAUTH_QUERY_PARAM);
      AnydoSentry.captureException(error);
      return await initUserAuthFromServer(auth);
    }
  }

  // No redirect params — clear any stale session so refresh starts fresh
  if (auth.isLoggedIn()) {
    await auth.logout();
  }
}

async function initUserAuthFromServer(auth: IAnydoAuth): Promise<void> {
  try {
    await auth.refreshUserFromServer();
  } catch {
    return await auth.logout();
  }
}

export function addAuthToken(url: string, accessToken?: string | null) {
  if (!accessToken) return url;
  const u = new URL(url);
  u.searchParams.set("token", accessToken);
  return u.href;
}
